class PackagingPlan {
  constructor(packagingVariants) {
    this.variantBySize = new Map();
    this.packagingVariantIds = new Set();
    packagingVariants.forEach((variant) => {
      this.packagingVariantIds.add(variant.id);
      const size = PackagingPlan.sizeOf(variant.title);
      if (size) this.variantBySize.set(size, variant.id);
    });
  }

  static sizeOf(value) {
    const match = /^\s*(\d+(?:[.,]\d+)?)\s*kg\s*$/i.exec(value || "");
    return match ? String(Number(match[1].replace(",", "."))) : null;
  }

  isPackaging(item) {
    return this.packagingVariantIds.has(item.variant_id);
  }

  sizeOfItem(item) {
    const values = (item.options_with_values || []).map((option) => option.value);
    for (const value of values) {
      const size = PackagingPlan.sizeOf(value);
      if (size) return size;
    }
    return null;
  }

  desiredQuantities(items) {
    const desired = new Map([...this.packagingVariantIds].map((id) => [id, 0]));
    items.forEach((item) => {
      if (this.isPackaging(item) || item.packaging_excluded) return;
      const variantId = this.variantBySize.get(this.sizeOfItem(item));
      if (variantId) desired.set(variantId, desired.get(variantId) + item.quantity);
    });
    return desired;
  }

  currentQuantities(items) {
    const current = new Map([...this.packagingVariantIds].map((id) => [id, 0]));
    items.forEach((item) => {
      if (this.isPackaging(item)) current.set(item.variant_id, current.get(item.variant_id) + item.quantity);
    });
    return current;
  }

  updatesFor(items) {
    const desired = this.desiredQuantities(items);
    const current = this.currentQuantities(items);
    const updates = {};
    desired.forEach((quantity, variantId) => {
      if (current.get(variantId) !== quantity) updates[variantId] = quantity;
    });
    return updates;
  }
}

class PackagingSync {
  static SOURCE = "packaging-sync";

  constructor(plan) {
    this.plan = plan;
    this.queue = Promise.resolve();
  }

  start() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && (event.source === PackagingSync.SOURCE || event.source === "product-form")) return;
      this.sync();
    });
    window.productFormHooks.afterAdd.push(this.afterAdd.bind(this));
    document.addEventListener("submit", this.onCheckoutSubmit.bind(this), true);
    this.sync();
  }

  enqueue(task) {
    const run = this.queue.then(task);
    this.queue = run.catch((error) => console.error("packaging-sync", error));
    return run;
  }

  sync() {
    return this.enqueue(async () => {
      const state = await this.reconcile(["cart-icon-bubble"]);
      if (!state) return;
      this.renderBubble(state.sections && state.sections["cart-icon-bubble"]);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: PackagingSync.SOURCE, cartData: state });
    });
  }

  afterAdd(addResponse, sections) {
    return this.enqueue(() => this.reconcile(sections))
      .then((state) => (state ? { ...state, id: addResponse.id } : addResponse))
      .catch(() => addResponse);
  }

  async fetchItems() {
    const html = await fetch(`${routes.root_url}?section_id=packaging-cart-data`).then((r) => r.text());
    const data = new DOMParser().parseFromString(html, "text/html").getElementById("PackagingCartData");
    return data ? JSON.parse(data.textContent) : [];
  }

  async reconcile(sections) {
    const items = await this.fetchItems();
    const updates = this.plan.updatesFor(items);
    if (Object.keys(updates).length === 0) return null;

    const response = await fetch(`${routes.cart_update_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({ updates, sections }),
    });
    const state = await response.json();
    if (!response.ok) throw new Error(state.description || response.statusText);
    return state;
  }

  renderBubble(html) {
    const bubble = document.getElementById("cart-icon-bubble");
    if (!bubble || !html) return;
    const section = new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section");
    if (section) bubble.innerHTML = section.innerHTML;
  }

  onCheckoutSubmit(event) {
    const submitter = event.submitter;
    if (!submitter || submitter.name !== "checkout" || submitter.dataset.packagingChecked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.sync().finally(() => {
      submitter.dataset.packagingChecked = "true";
      event.target.requestSubmit(submitter);
    });
  }
}

(() => {
  const config = document.getElementById("PackagingConfig");
  if (!config) return;
  const { variants } = JSON.parse(config.textContent);
  new PackagingSync(new PackagingPlan(variants)).start();
})();
